import * as SwitchPrimitive from '@radix-ui/react-switch';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';

export interface SwitchProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: ReactNode;
  description?: ReactNode;
}

/** Interruptor (role="switch") de 48×28 con etiqueta a la izquierda y fila táctil de 44 px. */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { label, description, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const switchId = id ?? autoId;
  const descId = description ? `${switchId}-desc` : undefined;
  return (
    <div className={cn('flex min-h-11 items-center justify-between gap-4 py-2', className)}>
      {label ? (
        <div className="min-w-0">
          <label htmlFor={switchId} className="block cursor-pointer text-base text-fg">
            {label}
          </label>
          {description ? (
            <p id={descId} className="text-sm text-fg-muted">
              {description}
            </p>
          ) : null}
        </div>
      ) : null}
      <SwitchPrimitive.Root
        ref={ref}
        id={switchId}
        aria-describedby={descId}
        className={cn(
          'flex h-7 w-12 shrink-0 items-center rounded-full border border-input bg-muted',
          'transition-[background-color,border-color] duration-base ease-standard',
          'disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary',
        )}
        {...rest}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block size-5 translate-x-0.5 rounded-full bg-card shadow-elev-1',
            'transition-transform duration-base ease-settle data-[state=checked]:translate-x-6',
          )}
        />
      </SwitchPrimitive.Root>
    </div>
  );
});
